import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import {
  Layers,
  Search,
  Filter,
  Command,
  Undo2,
  Sun,
  Zap,
  ShieldCheck,
  Keyboard,
  Database,
  Monitor,
  Mail,
} from 'lucide-react';

const features = [
  {
    icon: Layers,
    title: 'Multi-Tab Editing',
    description:
      'Open multiple CSV files with full state isolation. Each tab tracks its own undo history, filters, and unsaved changes.',
  },
  {
    icon: Search,
    title: 'Find & Replace',
    description:
      'Inline search across headers and cell values. Navigate matches with keyboard shortcuts, replace individually or all at once.',
    shortcut: '⌘F',
  },
  {
    icon: Filter,
    title: 'Per-Column Filtering',
    description:
      'Apply substring filters to any column. Combine multiple filters with AND logic, then export filtered views as new CSVs.',
  },
  {
    icon: Command,
    title: 'Command Palette',
    description:
      'Fuzzy-search every available action. Grouped by section with inline keyboard shortcut hints.',
    shortcut: '⌘K',
  },
  {
    icon: Undo2,
    title: 'Smart Undo/Redo',
    description:
      '50-step undo history with smart coalescing — rapid keystrokes merge into a single entry so you undo the whole burst, not character by character.',
  },
  {
    icon: Sun,
    title: 'Theme Support',
    description:
      'Light, Dark, or System — persisted across sessions. Follows macOS appearance changes in real time.',
  },
  {
    icon: Zap,
    title: 'Virtual Scrolling',
    description:
      'Only visible rows render to the DOM. A 100-row file and a 100,000-row file feel identical.',
  },
  {
    icon: ShieldCheck,
    title: 'Privacy-First',
    description:
      'No cloud uploads. No accounts. No analytics. No network requests. Your data never leaves your machine.',
  },
];

const principles = [
  {
    icon: Zap,
    title: 'Speed over features',
    description:
      'Opens in under a second. Large files parse in a background thread so the UI never locks.',
  },
  {
    icon: Keyboard,
    title: 'Keyboard-first, mouse-friendly',
    description:
      'Every action is reachable via shortcuts. Arrow keys navigate, Enter edits, and ⌘K opens the palette.',
  },
  {
    icon: Database,
    title: 'Data integrity by default',
    description:
      'Reads and writes plain text. No formula evaluation, no silent type coercion. What you see is what gets saved.',
  },
  {
    icon: Monitor,
    title: 'Native Mac experience',
    description:
      'Hidden titlebar with inset traffic lights, system theme integration, standard menu bar, and familiar ⌘ shortcuts.',
  },
];

const FeatureCard = ({ icon: Icon, title, description, shortcut }) => (
  <div className="ec-card p-6">
    <div className="flex items-start gap-4">
      <div className="ec-icon-badge">
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base font-semibold" style={{ color: 'var(--ec-text)' }}>
            {title}
          </h3>
          {shortcut && <span className="ec-kbd">{shortcut}</span>}
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--ec-text-secondary)' }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

const PrincipleCard = ({ icon: Icon, title, description, index }) => (
  <div className={`ec-animate ec-animate-d${index + 1}`}>
    <div className="flex items-start gap-4">
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
        style={{ background: 'rgba(37, 99, 235, 0.12)', color: 'var(--ec-accent)' }}
      >
        {index + 1}
      </div>
      <div>
        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--ec-text)' }}>
          {title}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--ec-text-secondary)' }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

const Rowly = () => {
  return (
    <div className="rowly-page min-h-screen">
      <SiteNav tone="dark" />
      {/* Hero */}
      <section className="ec-gradient-bg relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-6 pb-12 md:pb-16">
          <div className="flex flex-col items-center text-center">
            <div className="ec-animate">
              <img
                src="/easy-csv-icon.png"
                alt="Rowly"
                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl shadow-lg mb-8"
                style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
              />
            </div>

            <h1
              className="ec-animate ec-animate-d1 text-4xl md:text-6xl font-bold tracking-tight mb-4"
              style={{ color: 'var(--ec-text)' }}
            >
              Rowly
            </h1>

            <p
              className="ec-animate ec-animate-d2 text-lg md:text-xl max-w-2xl mb-4 leading-relaxed"
              style={{ color: 'var(--ec-text-secondary)' }}
            >
              Edit large CSVs quickly without uploading your data.
            </p>

            <p
              className="ec-animate ec-animate-d3 text-sm max-w-xl mb-10"
              style={{ color: 'var(--ec-text-muted)' }}
            >
              A native macOS editor that opens in under a second, handles large files smoothly,
              and keeps every file on your Mac.
            </p>

            <div className="ec-animate ec-animate-d4 flex flex-wrap gap-4 justify-center">
              <a
                href="https://apps.apple.com/us/app/easy-csv-fast-csv-editing/id6759234968?mt=12"
                target="_blank"
                rel="noopener noreferrer"
                className="ec-btn-primary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09 21.99C7.79 22.03 6.8 20.68 5.96 19.47C4.25 16.97 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.89C10.1 6.87 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                </svg>
                Download for Mac
              </a>
              <Link to="/rowly/privacy" className="ec-btn-secondary">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="ec-divider" />

      {/* Problem */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="ec-section-label mb-4 inline-block">The Problem</span>
          <h2
            className="text-2xl md:text-3xl font-bold tracking-tight"
            style={{ color: 'var(--ec-text)' }}
          >
            CSV editors force the wrong trade-offs
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            {
              label: 'Excel is overkill',
              detail:
                'Slow startup, formula misinterpretation, and the constant risk of silent data corruption — dates, leading zeros, long numbers.',
            },
            {
              label: 'Text editors are too raw',
              detail:
                'Navigating and editing a 50-column file as raw comma-delimited text is error-prone and tedious.',
            },
            {
              label: 'Web tools require upload',
              detail:
                'Cloud-based editors introduce privacy concerns and network dependency for files that should stay local.',
            },
          ].map((item) => (
            <div key={item.label} className="ec-card p-6 text-center">
              <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--ec-text)' }}>
                {item.label}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--ec-text-muted)' }}>
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="ec-divider" />

      {/* Features */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-16">
          <span className="ec-section-label mb-4 inline-block">Features</span>
          <h2
            className="text-2xl md:text-3xl font-bold tracking-tight"
            style={{ color: 'var(--ec-text)' }}
          >
            Fast, focused CSV editing
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      <div className="ec-divider" />

      {/* Principles */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-16">
          <span className="ec-section-label mb-4 inline-block">Design Principles</span>
          <h2
            className="text-2xl md:text-3xl font-bold tracking-tight"
            style={{ color: 'var(--ec-text)' }}
          >
            Designed for speed, control, and data integrity
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-10 max-w-3xl mx-auto">
          {principles.map((principle, i) => (
            <PrincipleCard key={principle.title} {...principle} index={i} />
          ))}
        </div>
      </section>

      <div className="ec-divider" />

      {/* Contact / Footer */}
      <footer style={{ background: 'rgba(2, 6, 23, 0.6)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-12">
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--ec-text)' }}>
                Rowly
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--ec-text-muted)' }}>
                Built by Teddy Marchildon
              </p>
              <a
                href="mailto:ted.marchildon@gmail.com"
                className="ec-link inline-flex items-center gap-2 text-sm"
              >
                <Mail size={14} />
                ted.marchildon@gmail.com
              </a>
            </div>

            <div className="flex flex-col gap-3 text-sm">
              <Link to="/rowly/privacy" className="ec-link">
                Privacy Policy
              </Link>
              <Link to="/" className="ec-link">
                teddymarchildon.com
              </Link>
            </div>
          </div>

          <div className="ec-divider my-10" />

          <p className="text-center text-xs" style={{ color: 'var(--ec-text-muted)' }}>
            &copy; {new Date().getFullYear()} Rowly. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Rowly;
